import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';
import Popup from '@databases/atlaskit/Popup';
import Z_INDEX from '@databases/layout/z-index';
import {
  useFocusState,
  useHoverState,
  useIsMounted,
  useStateWithOverride,
} from '@databases/react';
import { generateId } from '@databases/utils/utils';
import composeRefs from '@seznam/compose-react-refs';
import React, {
  type FunctionComponent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useAsync } from 'react-async';
import styled from 'styled-components';

import { useFormOptional } from './FormProvider';
import { type ValidationData } from './types';

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  font: ${token('font.body')};
  padding: ${token('space.200', '16px')} ${token('space.300', '24px')};

  > :first-child {
    margin-right: ${token('space.025')};
  }
`;

interface ValidationProps {
  isInvalid?: boolean;
}

export enum Placement {
  TOP = 'top',
  BOTTOM = 'bottom',
}

export const ValidationStatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export interface ValidationStatusProps {
  /** A string containing the error message if there is a validation error, otherwise undefined. */
  error: string | undefined | Promise<string | undefined>;
  children: (props: ValidationProps) => ReactNode;

  /**
   * If true, the field will be considered as "touched" immediately. Otherwise it is only considered
   * touched after the first blur event. Error messages are only shown for touched fields.
   */
  isTouched?: boolean;
  placement?: Placement;
}

/**
 * Will show a validation error as an inline dialog on hover/focus.
 * When used inside a Form context, will register the validation error with the context.
 */
const ValidationStatus: FunctionComponent<ValidationStatusProps> = ({
  error,
  children,
  isTouched: isTouchedProp,
  placement = Placement.TOP,
}) => {
  const [isTouched, setIsTouched] = useStateWithOverride(false, isTouchedProp);

  const form = useFormOptional();

  const id = useMemo(() => generateId(), []);

  const ref = useRef<HTMLDivElement>(null);

  const isMounted = useIsMounted();

  const touch = useCallback(() => {
    if (!isTouched && isMounted.current) {
      setIsTouched(true);
    }
  }, [isMounted, isTouched, setIsTouched]);

  const focus = useCallback(() => {
    (
      ref.current?.querySelector('input,textarea,select,[tabindex]') as
        | HTMLElement
        | undefined
    )?.focus();
  }, []);

  const [isFocus, focusProps] = useFocusState({ onBlur: touch });
  const [isHover, hoverProps] = useHoverState();

  const errorPromise = useMemo<Promise<string | undefined>>(
    () => Promise.resolve(error).catch((e) => e.message),
    [error]
  );
  const errorAsync = useAsync({
    promise: errorPromise,
  });
  const resolvedError =
    typeof error === 'object' && 'then' in error ? errorAsync.data : error;
  const isInvalid = resolvedError != null;

  const data = useMemo(
    (): ValidationData => ({
      id,
      isTouched,
      touch,
      focus,
      isInvalid,
      error: resolvedError,
      errorPromise,
    }),
    [errorPromise, focus, id, isInvalid, isTouched, resolvedError, touch]
  );

  useEffect(() => {
    if (form) {
      form._addValidationData(id, data);
    }
  }, [data, form, id]);

  useEffect(() => {
    return () => {
      if (form) {
        form._removeValidationData(id);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const props = useMemo(
    (): ValidationProps => ({
      isInvalid: isTouched ? isInvalid : undefined,
    }),
    [isInvalid, isTouched]
  );

  return (
    <Popup
      isOpen={isTouched && !!error && (isHover || isFocus)}
      content={() => (
        <ErrorMessage>
          <StatusErrorIcon
            label=""
            color={token('color.icon.danger', '#C9372C')}
            spacing={'spacious'}
          />
          {resolvedError}
        </ErrorMessage>
      )}
      placement={placement}
      zIndex={Z_INDEX.TOOLTIP}
      trigger={(triggerProps) => (
        <ValidationStatusContainer
          {...triggerProps}
          {...focusProps}
          {...hoverProps}
          ref={composeRefs(ref, triggerProps.ref)}
        >
          {children(props)}
        </ValidationStatusContainer>
      )}
      isTooltip
    />
  );
};

export default ValidationStatus;
